// ═══════════════════════════════════════════════════════════════════════
// data/db.js — Supabase data-access helpers (extracted from App.js)
// ═══════════════════════════════════════════════════════════════════════
// Centralises the workspace/channel/tier/cost read+write helpers so the React
// components don't issue raw Supabase queries inline. Moved VERBATIM from
// App.js (no behaviour change). RLS remains the security boundary.
import { supabase } from "../lib/supabaseClient";
import { cpcForDate } from "../lib/cpcAlloc";
import { normaliseCoFundRow, coFundRatesFor, coFundApplies, isMissingRelationError } from "../lib/coFund";

// PostgREST returns at most a fixed number of rows per request (Supabase's
// default cap is 1000) and gives NO error when it truncates — it silently drops
// the rest. For an unbounded account-wide select like "every daily_log" a
// data-rich brand (e.g. 4 locations × ~250 days ≈ 1000 rows) would load only
// the first page and silently show an UNDERSTATED P&L. fetchAllRows pages through
// with .range() until a short page proves we've reached the end, so the result is
// always complete regardless of row count. A stable .order() is required so the
// pages line up — PostgREST does not guarantee row order between requests, so
// without it pagination could duplicate or skip rows. orderCol must be a column
// that exists on the table; "id" (the PK) is a safe unique default.
const PG_PAGE_SIZE = 1000;
export async function fetchAllRows(makeQuery, orderCol = "id") {
  let all = [];
  for (let from = 0; ; from += PG_PAGE_SIZE) {
    const { data, error } = await makeQuery()
      .order(orderCol, { ascending: true })
      .range(from, from + PG_PAGE_SIZE - 1);
    if (error) return { data: null, error };
    const batch = data || [];
    all = all.concat(batch);
    if (batch.length < PG_PAGE_SIZE) break; // short (or empty) page → no more rows
  }
  return { data: all, error: null };
}

// ── Supabase DB helpers — match schema v2 ───────────────────────────
export async function loadWorkspacesFromDB(userId, accountId) {
  // Ensure we have a valid session before querying. We use getSession() — NOT
  // refreshSession() — deliberately.
  //
  // refreshSession() FORCES a refresh-token rotation on every call. When two
  // workspace loads race (e.g. the post-Stripe redirect fires the post-stripe
  // handler AND the session-restore effect on the fresh page load, both calling
  // this function), the second refresh uses an already-rotated token, fails,
  // and Supabase invalidates the session — the user gets silently logged out.
  // This was the root cause of the "creating a workspace logs me out" bug that
  // looked GBP-specific (GBP/UK users go through the real Stripe redirect, which
  // triggers the double-load; UAE free-override accounts skip the redirect and
  // never hit the race).
  //
  // getSession() returns the current session and lets the Supabase client's
  // built-in autoRefreshToken handle renewal internally (only when actually
  // near expiry, and without colliding rotations). Safe under concurrency.
  try {
    const { data: { session }, error: sessErr } = await supabase.auth.getSession();
    if (sessErr) {
      console.warn("[loadWorkspaces] getSession error:", sessErr.message);
    } else if (session?.user) {
      console.log("[loadWorkspaces] Session OK, user:", session.user.id);
    }
  } catch (e) {
    console.warn("[loadWorkspaces] getSession exception:", e);
  }

  // STEP 1: Load workspaces themselves (no joins).
  //
  // We prefer filtering by account_id (the modern, account-based model) over
  // user_id (legacy, single-user model). This is essential for operators —
  // an operator's workspaces belong to the brand owner's account, not to the
  // operator. Filtering by user_id would return nothing for them.
  //
  // RLS does further filtering automatically:
  //   - Brand owners see every workspace in their account.
  //   - Operators see only workspaces in their location_access list.
  //   - Super admins see everything.
  //
  // If no accountId is provided (e.g. user has no team context yet, or
  // pre-accounts legacy user), fall back to the user_id filter.
  let query = supabase.from("workspaces").select("*");
  if (accountId) {
    query = query.eq("account_id", accountId);
    console.log(`[loadWorkspaces] Scoping by account_id=${accountId}`);
  } else {
    query = query.eq("user_id", userId);
    console.log(`[loadWorkspaces] Scoping by user_id=${userId} (legacy / no account context)`);
  }
  const { data: workspaces, error: wsErr } = await query.order("created_at", { ascending: true });

  if (wsErr) {
    console.error("[loadWorkspaces] Workspace query FAILED:", wsErr);
    return null;
  }
  if (!workspaces?.length) {
    console.warn("[loadWorkspaces] No workspaces found for user", userId);
    return null;
  }
  console.log(`[loadWorkspaces] Loaded ${workspaces.length} workspaces`);

  const wsIds = workspaces.map(w => w.id);

  // STEP 2: Load channels separately (best-effort, doesn't fail the whole load)
  let channelsData = [];
  try {
    const { data: chs, error: chErr } = await fetchAllRows(
      () => supabase.from("channels").select("*").in("workspace_id", wsIds),
      "created_at" // stable fetch order; channels are re-sorted alphabetically per workspace below (drives the pills/dropdowns AND the derived platform string)
    );
    if (chErr) console.warn("[loadWorkspaces] Channels query failed:", chErr);
    else channelsData = chs || [];
  } catch (e) { console.warn("[loadWorkspaces] Channels exception:", e); }

  // STEP 3: Load channel_promo_tiers (best-effort)
  let tiersData = [];
  try {
    const { data: tiers, error: tErr } = await fetchAllRows(
      () => supabase.from("channel_promo_tiers").select("*").in("workspace_id", wsIds)
    );
    if (tErr) console.warn("[loadWorkspaces] Tiers query failed:", tErr);
    else tiersData = tiers || [];
  } catch (e) { console.warn("[loadWorkspaces] Tiers exception:", e); }

  // STEP 4: Load menu_items (best-effort)
  let menuData = [];
  try {
    const { data: items, error: mErr } = await fetchAllRows(
      () => supabase.from("menu_items").select("*").in("workspace_id", wsIds)
    );
    if (mErr) console.warn("[loadWorkspaces] Menu items query failed:", mErr);
    else menuData = items || [];
  } catch (e) { console.warn("[loadWorkspaces] Menu items exception:", e); }

  // STEP 5: Load daily_logs (best-effort)
  let logsData = [];
  try {
    const { data: logs, error: lErr } = await fetchAllRows(
      () => supabase.from("daily_logs").select("*").in("workspace_id", wsIds)
    );
    if (lErr) console.warn("[loadWorkspaces] Daily logs query failed:", lErr);
    else logsData = logs || [];
  } catch (e) { console.warn("[loadWorkspaces] Daily logs exception:", e); }

  // STEP 6: Load cpc_allocations (best-effort)
  let allocsData = [];
  try {
    const { data: allocs, error: aErr } = await fetchAllRows(() => supabase.from("cpc_allocations").select("*").in("workspace_id", wsIds));
    if (aErr) console.warn("[loadWorkspaces] CPC allocations query failed:", aErr);
    else allocsData = allocs || [];
  } catch (e) { console.warn("[loadWorkspaces] CPC allocations exception:", e); }

  // STEP 7: Load platform_cofunding — platform co-funded discounts (migration
  // 045). Rows are stamped per history row below (coFundRates).
  // NOT silently best-effort like the steps above: a missing table (PGRST205 /
  // 42P01, migration not applied) PROVES there are no entries, so [] is correct.
  // ANY other error/exception means the entries exist but could not be read, so
  // every figure would leave out the platform's share. Flag it
  // (ws.coFundLoadFailed) so the Daily Log warns and blocks adding a duplicate
  // entry against an empty-looking list (the backend fails closed the same way).
  let coFundData = [];
  let coFundLoadFailed = false;
  try {
    const { data: cfRows, error: cfErr } = await fetchAllRows(() => supabase.from("platform_cofunding").select("*").in("workspace_id", wsIds));
    if (cfErr) {
      if (isMissingRelationError(cfErr)) console.warn("[loadWorkspaces] platform_cofunding table not found — no co-fund entries:", cfErr);
      else { coFundLoadFailed = true; console.warn("[loadWorkspaces] Platform co-funding query failed:", cfErr); }
    } else coFundData = cfRows || [];
  } catch (e) {
    if (!isMissingRelationError(e)) coFundLoadFailed = true;
    console.warn("[loadWorkspaces] Platform co-funding exception:", e);
  }

  // Build result by joining in-memory
  const result = {};
  for (const ws of workspaces) {
    // Deterministic, alphabetical-by-platform channel order so every channel
    // listing (filter pills, dropdowns, per-platform breakdowns) is consistent
    // every load instead of following arbitrary DB insertion order. Object key
    // insertion order below is preserved by Object.values, so this single sort
    // propagates the order everywhere. (The "All" pill is added separately.)
    const wsChannels = channelsData
      .filter(c => c.workspace_id === ws.id)
      .sort((a, b) => String(a.platform || "").localeCompare(String(b.platform || ""), undefined, { sensitivity: "base" }));
    const wsMenu = menuData.filter(m => m.workspace_id === ws.id);
    const wsLogs = logsData.filter(l => l.workspace_id === ws.id);
    const wsTiers = tiersData.filter(t => t.workspace_id === ws.id);
    const wsAllocs = allocsData.filter(a => a.workspace_id === ws.id);
    const wsCoFund = coFundData.filter(r => r.workspace_id === ws.id);

    const channels = {};
    for (const ch of wsChannels) {
      const chAllocs = wsAllocs
        .filter(a => a.channel_id === ch.id)
        .map(a => ({ id: a.id, channelId: a.channel_id, dateFrom: a.date_from, dateTo: a.date_to, cpc: +a.cpc_amount || 0 }));
      const chCoFund = wsCoFund.filter(r => r.channel_id === ch.id).map(normaliseCoFundRow);

      const menu = wsMenu
        .filter(m => m.channel_id === ch.id || !m.channel_id)
        .map(m => ({ id:m.id, name:m.name, cost:+m.cost_price, price:+m.selling_price, disc:+m.discount_pct||0, fee:+m.campaign_fee||0, cap:+m.discount_cap||0 }));

      const channelLogs = wsLogs.filter(l => l.channel_id === ch.id);
      // A day's allocated CPC belongs to the channel-DAY, not to each log row.
      // Count rows per date so the per-day CPC is SPLIT across multiple same-day
      // entries (instead of being attached in full to each), otherwise duplicate
      // entries on a date would multiply that day's CPC in every period total.
      const logsPerDate = {};
      for (const l of channelLogs) logsPerDate[l.log_date] = (logsPerDate[l.log_date] || 0) + 1;
      const history = channelLogs
        .map(l => {
          const d = new Date(l.log_date + "T00:00:00");
          const mo = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][d.getMonth()];
          const grossVal = +l.gross || 0;
          const netVal = (l.net_sales != null && +l.net_sales > 0) ? +l.net_sales : grossVal;
          return {
            id: l.id, _dbId: l.id,
            // created_at drives the stable "order of entry" tiebreak when several
            // rows share the same log_date; kotNumber carries the delivery/order ref.
            createdAt: l.created_at || null,
            kotNumber: l.kot_number ?? null,
            dateObj: d,
            label: `${mo} '${String(d.getFullYear()).slice(2)}`,
            shortLabel: mo.slice(0,3),
            gross: grossVal,
            net: netVal,
            cpc: cpcForDate(chAllocs, l.log_date, ch.id) / (logsPerDate[l.log_date] || 1),
            // Platform co-funding: the winning entry's RATES for this raw log_date
            // (per ROW, never split). The amount is derived at use time (effNet /
            // coFundAmount in lib/coFund.js) so edits to gross/net stay correct.
            // A fed row (source 'feed') gets none: co-funding stays off on fed
            // rows (coFundApplies, checked again by the row helpers).
            coFundRates: coFundApplies(l) ? coFundRatesFor(chCoFund, l.log_date, ch.id) : null,
            campaignFee: +l.campaign_fee||0,
            commission: ch.commission != null ? +ch.commission : 0,
            cardFeePct: ch.card_fee_pct != null ? +ch.card_fee_pct : 0,
            // Where the row came from ('manual', 'scrape', 'statement' or
            // 'feed', migration 046). The Daily Log badges and locks 'feed'
            // rows on this alone (lib/dailyLogFeed.js), and lib/coFund.js's
            // row helpers give them no Platform contribution (coFundApplies).
            source: l.source ?? "manual",
          };
        });

      const promoTiers = wsTiers
        .filter(t => t.channel_id === ch.id)
        .sort((a,b)=>(a.sort_order||0)-(b.sort_order||0))
        .map(t => ({ id:t.id, label:t.label, discount:+t.discount_pct||0, fee:+t.campaign_fee||0, sort_order:+t.sort_order||0 }));

      channels[ch.id] = {
        id: ch.id, platform: ch.platform,
        color: ch.color,
        commission: ch.commission != null ? +ch.commission : 0,
        cardFeePct: ch.card_fee_pct != null ? +ch.card_fee_pct : 0,
        subscriptionFee: ch.subscription_fee != null ? +ch.subscription_fee : 0,
        history, menu, promoTiers,
        cpcAllocations: chAllocs,
        coFundAllocations: chCoFund,
      };
    }

    result[ws.id] = {
      id: ws.id, name: ws.name, city: ws.city||"", area: ws.area||"",
      market: ws.market||"uk", currency: ws.currency||"GBP",
      color: ws.color||"#1a73e8", status: ws.status||"trial",
      platform: Object.values(channels).map(c => c.platform).join(" + "),
      vatRate: ws.vat_rate != null ? +ws.vat_rate
        : (((ws.market||"uk") === "uk" || (ws.currency||"GBP") === "GBP") ? 0.20
          : (ws.market === "us" || ws.currency === "USD") ? 0
          : (ws.market === "in" || ws.currency === "INR") ? 0.05 : 0.05), // unset VAT => market default (UK/GBP 20%, UAE/AED 5%, US 0% — sales tax, IN 5% GST)
      cogsPct: ws.cogs_pct != null ? +ws.cogs_pct : 0,
      vatOnFees: ws.vat_on_fees === true,
      archived_at: ws.archived_at || null,
      cancel_at_period_end: ws.cancel_at_period_end === true,
      cancel_scheduled_for: ws.cancel_scheduled_for || null,
      fixedCosts: {
        rent: +ws.fixed_cost_rent||0, wages: +ws.fixed_cost_wages||0,
        utilities: +ws.fixed_cost_utilities||0, other: +ws.fixed_cost_other||0,
      },
      history: Object.values(channels).flatMap(c => c.history),
      menu: Object.values(channels).flatMap(c => c.menu),
      channels,
      // true = platform_cofunding could not be read this load (see STEP 7): the
      // figures exclude co-funding and the entry list is NOT known to be empty.
      coFundLoadFailed,
    };
  }
  return Object.keys(result).length > 0 ? result : null;
}

export async function createWorkspaceInDB(userId, wsData, channelsData) {
  // Resolve the user's account_id. The workspaces INSERT RLS policy requires
  // account_id IS NOT NULL AND has_active_access(account_id) — a workspace with
  // a NULL account_id is silently rejected. New trial accounts hit this:
  // the account row exists (trialing), but createWorkspaceInDB previously
  // omitted account_id, so the insert failed and the UI showed an empty state.
  let accountId = null;
  try {
    const { data: acct, error: acctErr } = await supabase
      .from("accounts")
      .select("id")
      .eq("owner_user_id", userId)
      .maybeSingle();
    if (acctErr) console.warn("createWorkspace: account lookup failed:", acctErr.message);
    accountId = acct?.id || null;
  } catch (e) {
    console.warn("createWorkspace: account lookup exception:", e);
  }
  if (!accountId) {
    // Without an account, the workspace can't satisfy RLS. Surface a clear
    // error rather than letting the insert silently fail.
    console.error("createWorkspace: no account found for user — cannot create workspace");
    return null;
  }

  // 1. Create workspace row
  const { data: ws, error: wsErr } = await supabase
    .from("workspaces")
    .insert({
      user_id:              userId,
      account_id:           accountId,
      name:                 wsData.name,
      city:                 wsData.city,
      area:                 wsData.area,
      market:               wsData.market,
      currency:             wsData.currency,
      color:                wsData.color,
      status:               "trial",
      vat_rate:             wsData.market === "uk" ? 0.20 : wsData.market === "us" ? 0 : wsData.market === "in" ? 0.05 : 0.05, // market-standard VAT default (UK 20%, UAE 5%, US 0% — sales tax, IN 5% GST)
      cogs_pct:             0,
      vat_on_fees:          true,
      fixed_cost_rent:      0,
      fixed_cost_wages:     0,
      fixed_cost_utilities: 0,
      fixed_cost_other:     0,
    })
    .select()
    .single();

  if (wsErr || !ws) { console.error("createWorkspace error:", wsErr); return null; }

  // 2. Create channels — commission and card fee default to 0; user must set in Configuration
  const chInserts = channelsData.map(ch => ({
    workspace_id: ws.id,
    platform:     ch.platform,
    color:        ch.color,
    commission:   ch.commission ?? 0,
    card_fee_pct: ch.cardFeePct ?? 0,
    is_active:    true,
  }));

  const { data: chs, error: chErr } = await supabase
    .from("channels")
    .insert(chInserts)
    .select();

  if (chErr) console.error("createChannels error:", chErr);

  // Auto-create default promo tiers for each new channel
  if (chs?.length) {
    // Campaign fees are in the WORKSPACE currency — India's rupee magnitudes
    // are ~100× GBP (same ₹100 ≈ £1 convention as the AI ad allowance).
    const tierFeeScale = wsData.market === "in" ? 100 : 1;
    const DEFAULT_TIERS = [
      { label: "No promotion", discount_pct: 0.00, campaign_fee: 0.00, sort_order: 0 },
      { label: "20% off",      discount_pct: 0.20, campaign_fee: 15.00 * tierFeeScale, sort_order: 1 },
      { label: "30% off",      discount_pct: 0.30, campaign_fee: 10.00 * tierFeeScale, sort_order: 2 },
      { label: "50% off",      discount_pct: 0.50, campaign_fee: 0.00,  sort_order: 3 },
      { label: "BOGO",         discount_pct: 0.50, campaign_fee: 0.00,  sort_order: 4 },
    ];
    const tierInserts = chs.flatMap(ch =>
      DEFAULT_TIERS.map(t => ({ ...t, channel_id: ch.id, workspace_id: ws.id }))
    );
    const { error: tErr } = await supabase.from("channel_promo_tiers").insert(tierInserts);
    if (tErr) console.error("createPromoTiers error:", tErr);
  }

  return { wsId: ws.id, channels: chs || [] };
}

export async function deleteWorkspaceFromDB(wsId) {
  const { error } = await supabase.from("workspaces").delete().eq("id", wsId);
  if (error) console.error("deleteWorkspace:", error);
}

// ── Channel promo tier helpers ───────────────────────────────────────
export async function saveChannelTier(wsId, channelId, tier) {
  const payload = {
    workspace_id: wsId,
    channel_id: channelId,
    label: tier.label,
    discount_pct: tier.discount || 0,
    campaign_fee: tier.fee || 0,
    sort_order: tier.sort_order || 0,
  };
  if (tier.id && !String(tier.id).startsWith("local_")) payload.id = tier.id;
  const { data, error } = await supabase.from("channel_promo_tiers").upsert(payload).select().single();
  if (error) console.error("saveChannelTier:", error);
  return data;
}

export async function deleteChannelTier(tierId) {
  if (!tierId || String(tierId).startsWith("local_")) return;
  const { error } = await supabase.from("channel_promo_tiers").delete().eq("id", tierId);
  if (error) console.error("deleteChannelTier:", error);
}

// ── Fixed costs helper ──────────────────────────────────────────────
export async function saveFixedCostsToDB(wsId, fixedCosts) {
  const { error } = await supabase.from("workspaces").update({
    fixed_cost_rent:      fixedCosts.rent || 0,
    fixed_cost_wages:     fixedCosts.wages || 0,
    fixed_cost_utilities: fixedCosts.utilities || 0,
    fixed_cost_other:     fixedCosts.other || 0,
  }).eq("id", wsId);
  if (error) console.error("saveFixedCosts:", error);
}

// ── Workspace rates (VAT + COGS) helper ─────────────────────────────
export async function saveWorkspaceRatesToDB(wsId, rates) {
  const { error } = await supabase.from("workspaces").update({
    vat_rate: rates.vatRate ?? 0,
    cogs_pct: rates.cogsPct ?? 0,
  }).eq("id", wsId);
  if (error) console.error("saveWorkspaceRates:", error);
}

// ── Mutators that RETURN the raw Supabase `{ error }` ────────────────
// Unlike the helpers above (which log + swallow), these are used by callers
// that branch on the error themselves, so we hand the result straight back.

// Channel rate update (commission / card fee / subscription fee).
export function updateChannelRates(channelId, rates) {
  return supabase.from("channels").update(rates).eq("id", channelId);
}

// Toggle a workspace's vat_on_fees flag.
export function setVatOnFees(wsId, value) {
  return supabase.from("workspaces").update({ vat_on_fees: value }).eq("id", wsId);
}

// Rename a workspace (brand-owner edit; RLS enforces ownership).
export function renameWorkspaceInDB(wsId, newName) {
  return supabase.from("workspaces").update({ name: newName }).eq("id", wsId);
}

// Super-admin: archive workspaces by id.
export function archiveWorkspaces(ids) {
  return supabase.from("workspaces")
    .update({ archived_at: new Date().toISOString() })
    .in("id", ids);
}

// Account row for a given owner user id (returns raw { data, error }).
export function getAccountByOwner(userId) {
  return supabase.from("accounts").select("*").eq("owner_user_id", userId).maybeSingle();
}
